import { useEffect, useMemo, useState } from 'react';

import {
  useFetchActiveConsentPolicyQuery,
  useRecordConsentMutation,
} from '../../ducks/consentApi';

import {
  ConsentCategories,
  ConsentDecision,
  StoredConsent,
  notifyConsentChange,
  readStoredConsent,
} from './useCookieConsent';

export type CategoryKey = 'necessary' | 'preferences' | 'analytics' | 'marketing';

export type DraftPreferences = {
  necessary: true;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentSource = 'banner' | 'preferences_modal' | 'account_settings';

export type ActivePolicy = {
  id: string;
  version_label?: string;
  effective_from?: string;
  categories?: Array<{ key: CategoryKey; label?: string; default?: boolean; locked?: boolean }>;
  privacy_policy_version?: string | null;
};

export const ALL_ON: DraftPreferences = {
  necessary: true,
  preferences: true,
  analytics: true,
  marketing: true,
};

export const NECESSARY_ONLY: DraftPreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

const METHOD_BY_DECISION: Record<ConsentDecision, 'banner_accept' | 'banner_reject' | 'preferences_save' | 'api'> = {
  accept_all: 'banner_accept',
  reject_all: 'banner_reject',
  custom: 'preferences_save',
  withdraw: 'api',
};

const draftFromStored = (stored: StoredConsent | null): DraftPreferences => {
  if (!stored) return ALL_ON;
  const c = stored.categories || {};
  return {
    necessary: true,
    preferences: !!c.preferences,
    analytics: !!c.analytics,
    marketing: !!c.marketing,
  };
};

const decisionForDraft = (d: DraftPreferences): ConsentDecision => {
  const optional = (['preferences', 'analytics', 'marketing'] as const).map(k => d[k]);
  if (optional.every(Boolean)) return 'accept_all';
  if (!optional.some(Boolean)) return 'reject_all';
  return 'custom';
};

const isCurrentForActivePolicy = (stored: StoredConsent | null, activePolicyId?: string): boolean =>
  !!stored && !!activePolicyId && stored.policy_version_id === activePolicyId;

export type UseConsentFormResult = {
  isReady: boolean;
  policy: ActivePolicy | undefined;
  stored: StoredConsent | null;
  draft: DraftPreferences;
  toggle: (key: Exclude<CategoryKey, 'necessary'>, checked: boolean) => void;
  resetDraftFromStored: () => void;
  acceptAll: () => Promise<boolean>;
  rejectAll: () => Promise<boolean>;
  saveCustom: () => Promise<boolean>;
  isSubmitting: boolean;
  hasCurrentConsent: boolean;
};

/** Centralised state + submit logic for the cookie consent UI. */
export const useConsentForm = (source: ConsentSource): UseConsentFormResult => {
  const { data: policy, isSuccess } = useFetchActiveConsentPolicyQuery() as {
    data: ActivePolicy | undefined;
    isSuccess: boolean;
  };
  const [recordConsent, { isLoading: isSubmitting }] = useRecordConsentMutation();

  const [stored, setStored] = useState<StoredConsent | null>(null);
  const [draft, setDraft] = useState<DraftPreferences>(ALL_ON);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const initial = readStoredConsent();
    setStored(initial);
    setDraft(draftFromStored(initial));
    setIsHydrated(true);
  }, []);

  const assetsAtConsent = useMemo(
    () => (policy?.privacy_policy_version ? { privacy_policy: policy.privacy_policy_version } : undefined),
    [policy?.privacy_policy_version]
  );

  const submit = async (prefs: DraftPreferences): Promise<boolean> => {
    if (!policy) return false;
    const decision = decisionForDraft(prefs);
    const categories: ConsentCategories = { ...prefs };

    try {
      await recordConsent({
        decision,
        categories,
        policy_version_id: policy.id,
        method: METHOD_BY_DECISION[decision],
        source,
        url: typeof window !== 'undefined' ? window.location.href : null,
        language: typeof navigator !== 'undefined' ? navigator.language || null : null,
        assets_at_consent: assetsAtConsent,
      }).unwrap();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.warn('cookie-consent record failed', e?.message || e);
      return false;
    }

    setStored(readStoredConsent());
    setDraft(prefs);
    notifyConsentChange();
    return true;
  };

  return {
    isReady: isHydrated && isSuccess && !!policy,
    policy,
    stored,
    draft,
    toggle: (key, checked) => setDraft(d => ({ ...d, [key]: checked })),
    resetDraftFromStored: () => setDraft(draftFromStored(stored)),
    acceptAll: () => submit(ALL_ON),
    rejectAll: () => submit(NECESSARY_ONLY),
    saveCustom: () => submit(draft),
    isSubmitting,
    hasCurrentConsent: isCurrentForActivePolicy(stored, policy?.id),
  };
};
